import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import SyncLicenseCard from '../../components/SyncLicenseCard';
import { 
  StudentsListSheet, 
  AnalyticsDialog, 
  QuestionsDialog,
  ClassesOverviewSheet 
} from '../../components/DashboardModals';
import { 
  UsersRound, 
  UserCheck,
  School, 
  ListChecks, 
  Activity,
  Award,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Volume2,
  Mic,
  BookOpen,
  PenLine,
  Film,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [moduleStats, setModuleStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [studentsSheetOpen, setStudentsSheetOpen] = useState(false);
  const [classesSheetOpen, setClassesSheetOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [questionsOpen, setQuestionsOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [overviewRes, statsRes] = await Promise.all([
        api.get('/statistics/overview'),
        api.get('/statistics?period=monthly')
      ]);
      setStats(overviewRes.data);
      setModuleStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (cardType) => {
    switch (cardType) {
      case 'teachers':
        navigate('/admin/teachers');
        break;
      case 'students':
        setStudentsSheetOpen(true);
        break;
      case 'classes':
        setClassesSheetOpen(true);
        break;
      case 'questions':
        setQuestionsOpen(true);
        break;
      case 'attempts':
        setAnalyticsOpen(true);
        break;
      case 'score':
        setAnalyticsOpen(true);
        break;
      default:
        break;
    }
  };

  const statCards = [
    { 
      label: 'Total Teachers', 
      value: stats?.total_teachers || 0, 
      icon: UsersRound, 
      color: 'bg-sky-500',
      bgColor: 'bg-sky-50 dark:bg-sky-900/20',
      hoverColor: 'hover:border-sky-500/50 hover:shadow-sky-500/20',
      action: 'teachers',
      actionLabel: 'Manage Teachers'
    },
    { 
      label: 'Total Students', 
      value: stats?.total_students || 0, 
      icon: UserCheck, 
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      hoverColor: 'hover:border-indigo-500/50 hover:shadow-indigo-500/20',
      action: 'students',
      actionLabel: 'View All Students'
    },
    { 
      label: 'Total Classes', 
      value: stats?.total_classes || 0, 
      icon: School, 
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
      hoverColor: 'hover:border-emerald-500/50 hover:shadow-emerald-500/20',
      action: 'classes',
      actionLabel: 'View Classes'
    },
    { 
      label: 'Total Questions', 
      value: stats?.total_questions || 0, 
      icon: ListChecks, 
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      hoverColor: 'hover:border-orange-500/50 hover:shadow-orange-500/20',
      action: 'questions',
      actionLabel: 'Questions Breakdown'
    },
    { 
      label: 'Total Attempts', 
      value: stats?.total_attempts || 0, 
      icon: Activity, 
      color: 'bg-rose-500',
      bgColor: 'bg-rose-50 dark:bg-rose-900/20',
      hoverColor: 'hover:border-rose-500/50 hover:shadow-rose-500/20',
      action: 'attempts',
      actionLabel: 'View Analytics'
    },
    { 
      label: 'Average Score', 
      value: `${stats?.average_score || 0}%`, 
      icon: Award, 
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      hoverColor: 'hover:border-purple-500/50 hover:shadow-purple-500/20',
      action: 'score',
      actionLabel: 'Performance Insights'
    },
  ];

  const moduleConfig = {
    tts: { icon: Volume2, color: 'text-cyan-500', bg: 'bg-gradient-to-br from-cyan-500/10 to-blue-500/10', label: 'Text to Speech' },
    stt: { icon: Mic, color: 'text-emerald-500', bg: 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10', label: 'Speech to Text' },
    mcq: { icon: BookOpen, color: 'text-amber-500', bg: 'bg-gradient-to-br from-amber-500/10 to-orange-500/10', label: 'Multiple Choice' },
    ftb: { icon: PenLine, color: 'text-orange-500', bg: 'bg-gradient-to-br from-orange-500/10 to-red-500/10', label: 'Fill the Blanks', isNew: true },
    vmcq: { icon: Film, color: 'text-purple-500', bg: 'bg-gradient-to-br from-purple-500/10 to-pink-500/10', label: 'Cinematic MCQ' }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 dark:text-white">
              Admin Dashboard
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Overview of your school's performance
            </p>
          </div>
          {/* AI Insights Badge */}
          <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/20 to-cyan-500/20 border border-violet-500/20">
            <Sparkles className="h-4 w-4 text-violet-400" />
            <span className="text-sm text-violet-300">AI-Powered Insights</span>
          </div>
        </div>

        {/* Stats Grid - Interactive Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={idx} 
                className={`stat-card cursor-pointer transition-all duration-300 border-2 border-transparent ${stat.hoverColor} hover:shadow-lg group`}
                onClick={() => handleCardClick(stat.action)}
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${stat.bgColor} transition-transform group-hover:scale-110`}>
                        <Icon className={`h-6 w-6 ${stat.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
                        <p className="text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 dark:text-slate-600 group-hover:text-violet-500 transition-colors" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-violet-500 dark:text-violet-400 font-medium flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      {stat.actionLabel}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* License Sync Card + Module Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SyncLicenseCard className="lg:col-span-1" />
          
          {/* Module Performance */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Module Performance (Last 30 Days)</span>
                <button 
                  onClick={() => setAnalyticsOpen(true)}
                  className="text-sm text-violet-500 hover:text-violet-400 font-medium flex items-center gap-1"
                >
                  View All <ChevronRight className="h-4 w-4" />
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {moduleStats.length > 0 ? (
                <div className="space-y-4">
                  {moduleStats.map((stat, idx) => {
                    const config = moduleConfig[stat.module_type] || moduleConfig.mcq;
                    const Icon = config.icon;
                    return (
                      <div 
                        key={idx} 
                        className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        onClick={() => setAnalyticsOpen(true)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${config.bg}`}>
                              <Icon className={`h-5 w-5 ${config.color}`} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {config.label}
                              </p>
                              <p className="text-sm text-slate-500">{stat.total_attempts || 0} attempts</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-slate-900 dark:text-white">
                              {Math.round(stat.average_score || 0)}%
                            </p>
                            <p className="text-xs text-slate-500">avg score</p>
                          </div>
                        </div>
                        <Progress value={stat.average_score || 0} className="h-1.5" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">No data available yet</p>
                  <p className="text-xs text-slate-400 mt-1">Data will appear once students start taking tests</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Recommendations Section */}
        <Card className="bg-gradient-to-r from-violet-500/5 to-cyan-500/5 border-violet-500/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-500" />
              AI Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  title: 'Add More Content',
                  description: stats?.total_questions < 50 
                    ? 'Consider adding more questions to enrich the learning experience.'
                    : 'Great question variety! Keep adding diverse content.',
                  color: 'text-cyan-500',
                  bgColor: 'bg-cyan-500/10'
                },
                {
                  title: 'Student Engagement',
                  description: stats?.total_attempts < 100
                    ? 'Encourage more students to practice using the lab modules.'
                    : 'Excellent engagement! Students are actively learning.',
                  color: 'text-emerald-500',
                  bgColor: 'bg-emerald-500/10'
                },
                {
                  title: 'Performance Focus',
                  description: (stats?.average_score || 0) < 60
                    ? 'Average score is below 60%. Consider reviewing difficult topics.'
                    : 'Students are performing well overall. Keep up the good work!',
                  color: 'text-amber-500',
                  bgColor: 'bg-amber-500/10'
                }
              ].map((rec, idx) => (
                <div key={idx} className={`p-4 rounded-xl ${rec.bgColor} border border-white/10`}>
                  <p className={`font-medium ${rec.color} mb-1`}>{rec.title}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{rec.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <StudentsListSheet 
        open={studentsSheetOpen} 
        onOpenChange={setStudentsSheetOpen}
      />
      <ClassesOverviewSheet
        open={classesSheetOpen}
        onOpenChange={setClassesSheetOpen}
      />
      <AnalyticsDialog 
        open={analyticsOpen} 
        onOpenChange={setAnalyticsOpen}
        title="Performance Analytics"
      />
      <QuestionsDialog 
        open={questionsOpen} 
        onOpenChange={setQuestionsOpen}
      />
    </DashboardLayout>
  );
};

export default AdminDashboard;
